export interface OverlayPoints {
  topleft: [number, number];
  topright: [number, number];
  bottomleft: [number, number];
  bottomright: [number, number];
  center: [number, number];
}

export function computeBottomRight(
  topleft: [number, number],
  topright: [number, number],
  bottomleft: [number, number]
): [number, number] {
  return [
    topright[0] + (bottomleft[0] - topleft[0]),
    topright[1] + (bottomleft[1] - topleft[1]),
  ];
}

export function computeCenter(
  topleft: [number, number],
  topright: [number, number],
  bottomleft: [number, number]
): [number, number] {
  const br = computeBottomRight(topleft, topright, bottomleft);
  return [(topleft[0] + br[0]) / 2, (topleft[1] + br[1]) / 2];
}

export function computeAllPoints(
  topleft: [number, number],
  topright: [number, number],
  bottomleft: [number, number]
): OverlayPoints {
  const bottomright = computeBottomRight(topleft, topright, bottomleft);
  const center: [number, number] = [(topleft[0] + bottomright[0]) / 2, (topleft[1] + bottomright[1]) / 2];
  return { topleft, topright, bottomleft, bottomright, center };
}

export function rotatePoint(
  point: [number, number],
  center: [number, number],
  angleDeg: number
): [number, number] {
  const rad = (angleDeg * Math.PI) / 180;
  const cosLat = Math.cos((center[0] * Math.PI) / 180) || 1;
  const dx = (point[1] - center[1]) * cosLat;
  const dy = point[0] - center[0];

  const rotX = dx * Math.cos(rad) - dy * Math.sin(rad);
  const rotY = dx * Math.sin(rad) + dy * Math.cos(rad);

  return [center[0] + rotY, center[1] + rotX / cosLat];
}

export function rotateOverlay(
  topleft: [number, number],
  topright: [number, number],
  bottomleft: [number, number],
  angleDeg: number
): { topleft: [number, number]; topright: [number, number]; bottomleft: [number, number] } {
  const center = computeCenter(topleft, topright, bottomleft);
  return {
    topleft: rotatePoint(topleft, center, angleDeg),
    topright: rotatePoint(topright, center, angleDeg),
    bottomleft: rotatePoint(bottomleft, center, angleDeg),
  };
}

export function panOverlay(
  topleft: [number, number],
  topright: [number, number],
  bottomleft: [number, number],
  dLat: number,
  dLng: number
): { topleft: [number, number]; topright: [number, number]; bottomleft: [number, number] } {
  return {
    topleft: [topleft[0] + dLat, topleft[1] + dLng],
    topright: [topright[0] + dLat, topright[1] + dLng],
    bottomleft: [bottomleft[0] + dLat, bottomleft[1] + dLng],
  };
}

export function scaleOverlay(
  topleft: [number, number],
  topright: [number, number],
  bottomleft: [number, number],
  scaleFactor: number
): { topleft: [number, number]; topright: [number, number]; bottomleft: [number, number] } {
  const center = computeCenter(topleft, topright, bottomleft);
  const scale = (pt: [number, number]): [number, number] => [
    center[0] + (pt[0] - center[0]) * scaleFactor,
    center[1] + (pt[1] - center[1]) * scaleFactor,
  ];
  return {
    topleft: scale(topleft),
    topright: scale(topright),
    bottomleft: scale(bottomleft),
  };
}

export function tiltOverlay(
  topleft: [number, number],
  topright: [number, number],
  bottomleft: [number, number],
  tiltFactor: number
): { topleft: [number, number]; topright: [number, number]; bottomleft: [number, number] } {
  // Tilt tilts the vertical axis relative to horizontal
  const vX = [topright[0] - topleft[0], topright[1] - topleft[1]];
  const shiftLat = vX[0] * tiltFactor;
  const shiftLng = vX[1] * tiltFactor;
  return {
    topleft: [topleft[0] - shiftLat / 2, topleft[1] - shiftLng / 2],
    topright: [topright[0] - shiftLat / 2, topright[1] - shiftLng / 2],
    bottomleft: [bottomleft[0] + shiftLat / 2, bottomleft[1] + shiftLng / 2],
  };
}

export function createDefaultOverlayBounds(
  centerLat: number,
  centerLng: number,
  latSpan = 0.002,
  lngSpan = 0.003
): { topleft: [number, number]; topright: [number, number]; bottomleft: [number, number] } {
  return {
    topleft: [centerLat + latSpan / 2, centerLng - lngSpan / 2],
    topright: [centerLat + latSpan / 2, centerLng + lngSpan / 2],
    bottomleft: [centerLat - latSpan / 2, centerLng - lngSpan / 2],
  };
}
